// ============================================================
// FICHIER : StoryUS.tsx
// Scrollytelling "US RAA Story" — sections scellées sur le contenu du
// PDF US_RAA_Storytelling2026. Composant original (pas une copie du
// Story.tsx India, qui est spécifique à l'histoire UDAN), mais reprend
// le même langage visuel ATR (fond New Moon, accents turquoise/rouge,
// grandes stats animées, sections plein écran).
// ============================================================

import { useEffect, useRef, useState } from "react";
import FooterPage from "../../components/FooterPage";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Page } from "../../App";
import {
  FocusCity,
  GCD_NM_MAX,
  ROUTES_YEAR_MIN,
  ROUTES_YEAR_MAX,
  ROUTES_YEAR_DEFAULT,
  ROUTES_DISTANCE_MIN,
  ROUTES_DISTANCE_MAX,
} from "../../config/carto.us";
import {
  USHexFilters,
  fetchUSMetroAreas,
  fetchUSCatchments,
  fetchUSRouteStatuses,
  fetchUSRouteYears,
  USRouteFilters,
} from "../../layers/fetchLayers.us";
import MobilityMarketUS, { MobilityMarketUSHandle } from "../../components/MobilityMarketUS";
import RoutesMapUS from "../../components/RoutesMapUS";
import USCitySearch from "../../components/USCitySearch";
import "./Story_US_laptop_adapted.css";
import { cartoBasemapUrl } from "../../config/carto";
import FocusCitiesMapUS from "../../components/FocusCitiesMapUS";

// ─── Compteur animé, déclenché à l'entrée dans le viewport ───
function CountUp({
  target,
  suffix = "",
  prefix = "",
  decimals = 0,
  duration = 1400,
  trigger,
}: {
  target: number;
  suffix?: string;
  prefix?: string;
  decimals?: number;
  duration?: number;
  trigger: boolean;
}) {
  const [val, setVal] = useState(0);
  const raf = useRef<number | null>(null);

  useEffect(() => {
    if (!trigger) return;
    const start = performance.now();
    const step = (ts: number) => {
      const p = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(target * eased);
      if (p < 1) raf.current = requestAnimationFrame(step);
      else setVal(target);
    };
    raf.current = requestAnimationFrame(step);
    return () => {
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, [trigger, target, duration]);

  return (
    <>
      {prefix}
      {decimals > 0 ? val.toFixed(decimals) : Math.round(val).toLocaleString()}
      {suffix}
    </>
  );
}

/* fonction bas de page footer*/
const handleBackToTop = () => {
  const scrollContainer = document.querySelector(".rus-scroll");

  scrollContainer?.scrollTo({
    top: 0,
    behavior: "smooth",
  });
};

// ─── Wrapper de section avec fade/slide-in au scroll ───
function Section({
  id,
  className = "",
  children,
  onVisible,
}: {
  id: string;
  className?: string;
  children: (visible: boolean) => React.ReactNode;
  onVisible?: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          onVisible?.();
        }
      },
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section id={id} ref={ref} className={`rus-section ${className} ${visible ? "rus-in" : ""}`}>
      {children(visible)}
    </section>
  );
}

// ─── Chart "US inter-city mobility market" ───
// Axe + grille : lignes et labels générés à partir de la MÊME formule de
// position (topOffset), donc toujours alignés au pixel près — pas de
// background-image approximatif. De la hauteur est explicitement réservée
// au-dessus des barres pour l'avion/la valeur, qui ne peuvent donc jamais
// déborder sur la légende, quelle que soit la hauteur de la barre la plus
// haute (8B = 100% de l'échelle).
const BAR_MAX_PX = 200; // hauteur de la zone de traçage (représente 0–8B)
const AXIS_STEPS = [8, 7, 6, 5, 4, 3, 2, 1, 0];
const topOffset = (step: number) => ((8 - step) / 8) * BAR_MAX_PX;

const MARKET_DATA = [
  { key: "usa", label: "Total", air: 10, airB: 0.8, groundB: 7.2, totalB: 8, showPlane: false },
  { key: "below", label: "Below 400NM", air: 4, airB: 0.24, groundB: 5.76, totalB: 6, showPlane: false },
  { key: "above", label: "Above 400NM", air: 30, airB: 0.8, groundB: 1.2, totalB: 2, showPlane: false },
];

function MarketChart({ active }: { active: boolean }) {
  const [hover, setHover] = useState<string | null>(null);
  const [isolate, setIsolate] = useState<"air" | "ground" | null>(null);

  const pxFor = (valueB: number) => (valueB / 8) * BAR_MAX_PX;

  return (
    <div className="rus-mkt-wrap">
      <div className="rus-chart-title">US one-way trips per year</div>

      <div className="rus-legend-row rus-legend-row--interactive">
        <button
          className={`rus-legend-btn ${isolate === "ground" ? "rus-legend-btn--dim" : ""} ${isolate === "air" ? "rus-legend-btn--active" : ""}`}
          onClick={() => setIsolate(isolate === "air" ? null : "air")}
        >
          <span className="rus-legend-swatch rus-legend-swatch--air"><span className="rus-legend-dot" /></span>
          Air mobility
        </button>
        <button
          className={`rus-legend-btn ${isolate === "air" ? "rus-legend-btn--dim" : ""} ${isolate === "ground" ? "rus-legend-btn--active" : ""}`}
          onClick={() => setIsolate(isolate === "ground" ? null : "ground")}
        >
          <span className="rus-legend-swatch rus-legend-swatch--ground"><span className="rus-legend-dot" /></span>
          Ground mobility
        </button>
      </div>

      {/* padding-top réserve la place pour l'avion + la valeur au-dessus
          de la barre la plus haute — elle ne peut jamais toucher la légende. */}
      <div className="rus-mkt-plotwrap">
        <div className="rus-mkt-plot" style={{ height: `${BAR_MAX_PX}px` }}>
          {/* ── Axe Y ── */}
          <div className="rus-mkt-axis">
            {AXIS_STEPS.map((step, idx) => (
              <span
                key={step}
                className={`rus-mkt-axis-label ${active ? "rus-mkt-axis-label--in" : ""}`}
                style={{ top: `${topOffset(step)}px`, transitionDelay: `${idx * 0.03}s` }}
              >
                {step}B
              </span>
            ))}
          </div>

          {/* ── Zone de tracé ── */}
          <div className="rus-mkt-barswrap">
            {/* Grille — mêmes offsets que l'axe, donc alignement garanti */}
            <div className="rus-mkt-gridlines">
              {AXIS_STEPS.map((step, idx) => (
                <span
                  key={step}
                  className={`rus-mkt-gridline ${step === 0 ? "rus-mkt-gridline--base" : ""} ${active ? "rus-mkt-gridline--in" : ""}`}
                  style={{ top: `${topOffset(step)}px`, transitionDelay: `${idx * 0.03}s` }}
                />
              ))}
            </div>

            {/* Barres */}
            <div className="rus-mkt-bars">
              {MARKET_DATA.map((b, i) => {
                const isHover = hover === b.key;
                const groundH = active ? pxFor(b.groundB) : 0;
                const airH = active ? pxFor(b.airB) : 0;
                const delay = `${i * 0.12}s`;

                return (
                  <div
                    className={`rus-mkt-col ${active ? "rus-mkt-col--in" : ""}`}
                    key={b.key}
                    style={{ transitionDelay: delay }}
                    onMouseEnter={() => setHover(b.key)}
                    onMouseLeave={() => setHover(null)}
                  >
                    <div className={`rus-mkt-stack ${isHover ? "rus-mkt-stack--hover" : ""}`}>
                      <div className="rus-mkt-topper">
                       
                        <span className="rus-mkt-value" style={{ transitionDelay: `calc(${delay} + 0.9s)` }}>
                          <CountUp target={b.totalB} suffix="B" trigger={active} duration={1200} />
                        </span>
                      </div>

                      {isHover && (
                        <div className="rus-mkt-tooltip">
                          <div className="rus-mkt-tooltip-row"><span style={{ background: "#56C177" }} /> {b.airB.toFixed(2)}B</div>
                          <div className="rus-mkt-tooltip-row"><span style={{ background: "#009A84" }} /> {b.groundB.toFixed(2)}B</div>
                        </div>
                      )}

                      <div
                        className="rus-mkt-seg rus-mkt-seg--ground"
                        style={{ height: `${groundH}px`, opacity: isolate === "air" ? 0.15 : 1, transitionDelay: delay }}
                      />
                      <div
                        className="rus-mkt-seg rus-mkt-seg--air"
                        style={{ height: `${airH}px`, opacity: isolate === "ground" ? 0.15 : 1, transitionDelay: delay }}
                      >
                        <span className="rus-mkt-pct">{b.air}%</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Labels de catégorie — alignés sous chaque colonne via le même
            décalage gauche que .rus-mkt-barswrap (largeur de l'axe) ── */}
        <div className="rus-mkt-labels">
          <div className="rus-mkt-labels-spacer" />
          <div className="rus-mkt-labels-row">
            {MARKET_DATA.map((b) => (
              <div key={b.key} className="rus-mkt-label">{b.label}</div>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
}

// ─── Fond de carte CARTO live (non-interactif) pour les sections "statement" ───
// Même tuile dark_all que Map.tsx / AirportAccess (India). Le ref de map est
// gardé accessible via `onReady` pour brancher de futurs layers (routes,
// hexagones...) par-dessus, sans avoir à remonter tout le composant.
function StatementBasemap({
  id,
  center,
  zoom = 4,
  onReady,
}: {
  id: string;
  center: [number, number];
  zoom?: number;
  onReady?: (map: L.Map) => void;
}) {
  const mapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (mapRef.current) return;
    const map = L.map(id, {
      center,
      zoom,
      zoomControl: false,
      dragging: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      boxZoom: false,
      keyboard: false,
      touchZoom: false,
      attributionControl: false,
    });


    L.tileLayer(cartoBasemapUrl()).addTo(map);

/* voile SUR la basemap, SOUS les données */
map.createPane("basemapTintPane");

const tintPane = map.getPane("basemapTintPane");
if (tintPane) {
  tintPane.style.zIndex = "350";
  tintPane.style.pointerEvents = "none";
}

L.rectangle(
  [
    [-85, -180],
    [85, 180],
  ],
  {
    pane: "basemapTintPane",
    stroke: false,
    fillColor: "#00313C",
    fillOpacity: 0.35,
    interactive: false,
  }
).addTo(map);

    mapRef.current = map;
    onReady?.(map);
    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div id={id} className="rus-basemap" />;
}

// ─── Slider année unique — "year_ref", pilote la snapshot du réseau de
// routes affichée sur la carte. ───
function YearSlider({
  year,
  min = ROUTES_YEAR_MIN,
  max = ROUTES_YEAR_MAX,
  onChange,
}: {
  year: number;
  min?: number;
  max?: number;
  onChange: (year: number) => void;
}) {
  const pct = ((year - min) / (max - min)) * 100;
  return (
    <div className="rus-gcd">
      <div className="rus-gcd-label">Year</div>
      <div className="rus-gcd-track-wrap">
        <div className="rus-gcd-track" />
        <div className="rus-gcd-range" style={{ left: "0%", right: `${100 - pct}%` }} />
        <input
          type="range"
          min={min}
          max={max}
          value={year}
          onChange={(e) => onChange(Number(e.target.value))}
          className="rus-gcd-input"
        />
      </div>
      <div className="rus-gcd-values">
        <span>{min}</span>
        <span className="rus-year-current">{year}</span>
        <span>{max}</span>
      </div>
    </div>
  );
}

// ─── Section 2 "800 routes closed" — reproduit l'architecture plein
// écran de Section 3 (MobilityMonitor) : carte 2D interactive (pas 3D,
// contrairement à la carte du marché de mobilité) en fond, texte en
// haut à gauche, "800" en évidence + filtres (Distance / Route status /
// Year) en bas à gauche. ───
function RoutesEvolutionStep({ active }: { active: boolean }) {
  const [distanceFrom, setDistanceFrom] = useState(ROUTES_DISTANCE_MIN);
  const [distanceTo, setDistanceTo] = useState(ROUTES_DISTANCE_MAX);
  const [year, setYear] = useState(ROUTES_YEAR_DEFAULT);
  const [yearMin, setYearMin] = useState(2000);;
  const [yearMax, setYearMax] = useState(ROUTES_YEAR_MAX);
  const [statusSelected, setStatusSelected] = useState<string[]>([""]); // [] = "All selected" so here we select stopped
  const [statusOptions, setStatusOptions] = useState<{ value: string; label: string }[]>([]);
  const [statusLoading, setStatusLoading] = useState(true);
  const [metaError, setMetaError] = useState<string | null>(null);
  const [routeStats, setRouteStats] = useState({ routeCount: 0, loading: true });

  // Charge en direct les statuts ET les années réellement présentes dans la
  // table — au lieu de deviner (ROUTES_YEAR_DEFAULT pouvait tomber sur une
  // année qui n'existe pas dans year_ref, ce qui faisait silencieusement
  // "0 route" en permanence sans jamais rien afficher). Toute erreur ici
  // (403, colonne inexistante, etc.) est maintenant affichée plutôt
  // qu'avalée silencieusement dans un [] qui ressemble à "All selected".
  useEffect(() => {
    let cancelled = false;

    fetchUSRouteStatuses()
      .then((vals) => {
    
        if (cancelled) return;
        if (vals.length === 0) {
          setMetaError((prev) => prev ?? "No route_type values returned — check table access/column name.");
        }
        setStatusOptions(vals.map((v) => ({ value: v, label: v })));
        setStatusLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setMetaError(e.message || "Failed to load route status list");
        setStatusOptions([]);
        setStatusLoading(false);
      });

    fetchUSRouteYears()
      .then((years) => {
        if (cancelled || years.length === 0) return;
        const min = Math.min(...years);
        const max = Math.max(...years);
        setYearMin(2000);
        setYearMax(max);
        setYear(max); // par défaut, l'année la plus récente disponible — garantie non-vide
      })
      .catch(() => {
        // silencieux ici uniquement : le fallback (ROUTES_YEAR_MIN/MAX/DEFAULT)
        // reste utilisable, l'erreur de statuts ci-dessus suffit à alerter.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const filters: USRouteFilters = {
    distanceFrom,
    distanceTo: 400,
    year,
    routeStatus: ["stopped routes"],
  };
/* moins en haut des textbox*/

const [textCollapsed, setTextCollapsed] = useState(false);
  return (
    <div className="rus-mmfull-wrap">
      <RoutesMapUS filters={filters} onStats={setRouteStats} />
      <div className="rus-mmfull-overlay" />


      {/* ── Texte en haut à gauche ── */}
      <div
  className={`rus-mmfull-text ${active ? "rus-in-up" : ""} ${
  textCollapsed ? "rus-mmfull-text--collapsed" : ""
}`}

  onWheel={(e) => {
    e.stopPropagation();
  }}

  onMouseDown={(e) => {
    e.stopPropagation();
  }}

  onClick={(e) => {
    e.stopPropagation();
  }}

  onDoubleClick={(e) => {
    e.stopPropagation();
  }}
>
   <button
    className="rus-text-collapse"
    onClick={() => setTextCollapsed((v) => !v)}
    aria-label={textCollapsed ? "Afficher le texte" : "Réduire le texte"}
  >
    {textCollapsed ? "+" : "−"}
  </button>
        <div className="rus-kicker">A regional decline</div>
        <h2 className="rus-h2">800 regional routes closed since 2000</h2>
        <p className="rus-p">
           Why does air travel capture such a small share of regional mobility demand?
        </p>
        <p className="rus-p">
         Several factors have contributed to this trend, including selection of aircraft types suited to longer routes and airline mergers causing consolidation around large hubs.
        </p>
        <p className="rus-p"> Consequently, the regional air network has been steadily contracted. Since 2000, <strong>more than 800 routes</strong> that provided regional air connections <strong> have closed</strong>.
        </p>

    <p className="rus-p">
        Every route closed means regional air market volume lost to personal cars and personal time lost in traffic and on roads. 
      </p>

      </div>

      <div className={`rus-mmfull-bottom ${active ? "rus-in-up" : ""}`}>

  <div className="rus-mmfull-filters">
  
    <YearSlider
      year={year}
      min={yearMin}
      max={yearMax}
      onChange={setYear}
    />
  </div>

  <div className="rus-mmfull-kf">
    <div className="rus-mmfull-kf-row">
      


    </div>
  </div>

</div>

        <div className="rus-mmfull-filters">
          
  
          <div className="rus-mmfull-filters-divider" />

          <GcdSlider
            label="Distance (NM)"
            from={distanceFrom}
            to={distanceTo}
            min={ROUTES_DISTANCE_MIN}
            max={ROUTES_DISTANCE_MAX}
            onChange={(f, t) => { setDistanceFrom(f); setDistanceTo(t); }}
          />

          <div className="rus-mmfull-filters-divider" />

          <YearSlider year={year} min={yearMin} max={yearMax} onChange={setYear} />
        </div>
      </div>
  
  );
}



// ─── Chart "Hub bias" — DEUX graphes distincts et complets (axe + graduations
// propres à chacun), sélectionnés via deux onglets clairs, un seul affiché à
// la fois. Réutilise l'architecture éprouvée du chart Section 1 (lignes de
// grille + labels générés depuis la MÊME formule de position -> alignement
// garanti, espace réservé au-dessus des barres pour que rien ne chevauche
// jamais). Icône avion "turboprop" sur la barre "Domestic air mobility",
// comme sur le PDF de référence (PAS le logo ATR). ───
const HUB_COLORS = ["#009A84", "#7fcfc0", "#D9C700", "#e9e4b8"];
const HUB_CATS = ["Large", "Medium", "Small", "Not a hub "];
const HUB_MAX_PX = 220;

// Une seule source de vérité : le % par catégorie (exactement comme sur le
// PDF) + le total en milliards. Les segments en milliards sont dérivés de
// total × %, donc les deux vues restent parfaitement cohérentes.
const HUB_ROWS = [
  { key: "all", label: "Mobility patterns", totalB: 7.0, pct: [50, 23, 17, 10], showPlane: false },
  { key: "air", label: "Air travellers", totalB: 0.8, pct: [90, 6, 3, 1], showPlane: true },
];

const HUB_MODES = {
  billion: { title: "Mobility patterns vs air travellers", max: 7, step: 1, suffix: "B" },
  percent: { title: "Mobility patterns vs air travellers", max: 100, step: 10, suffix: "%" },
};

function HubBiasChart({ active }: { active: boolean }) {
  const [mode, setMode] = useState<"billion" | "percent">("percent");
  const [hover, setHover] = useState<string | null>(null);
  const cfg = HUB_MODES[mode];

  const axisSteps: number[] = [];
  for (let s = cfg.max; s >= 0; s -= cfg.step) axisSteps.push(s);
  const topOffset = (step: number) => ((cfg.max - step) / cfg.max) * HUB_MAX_PX;

  const fmtAxis = (n: number) => (mode === "billion" ? `${n.toFixed(1)}B` : `${n}%`);

  return (
    <div className="rus-mkt-wrap">
      <div className=" rus-hub-chart-title">{cfg.title}</div>
      

      {/* ── Sélecteur de vue — plus visible, avec label pour guider l'utilisateur ── */}
      <div className="rus-hub-toggle-wrap">
        <span className="rus-hub-toggle-label">View</span>
        <div className="rus-hub-toggle">
          <button
            className={`rus-hub-toggle-btn ${mode === "billion" ? "rus-hub-toggle-btn--active" : ""}`}
            onClick={() => setMode("billion")}
          >
            Billion trips/year
          </button>
          <button
            className={`rus-hub-toggle-btn ${mode === "percent" ? "rus-hub-toggle-btn--active" : ""}`}
            onClick={() => setMode("percent")}
          >
            Percentage
          </button>
        </div>
      </div>

      <div className="rus-mkt-plotwrap">
        <div className="rus-mkt-plot" style={{ height: `${HUB_MAX_PX}px` }}>
          {/* ── Axe Y — même formule que la grille, alignement garanti ── */}
          <div className="rus-mkt-axis">
            {axisSteps.map((step) => (
              <span
                key={step}
                className={`rus-mkt-axis-label ${active ? "rus-mkt-axis-label--in" : ""}`}
                style={{ top: `${topOffset(step)}px` }}
              >
                {fmtAxis(step)}
              </span>
            ))}
          </div>

          <div className="rus-mkt-barswrap">
            {/* Grille — mêmes offsets que l'axe, donc alignement garanti */}
            <div className="rus-mkt-gridlines">
              {axisSteps.map((step) => (
                <span
                  key={step}
                  className={`rus-mkt-gridline ${step === 0 ? "rus-mkt-gridline--base" : ""} ${active ? "rus-mkt-gridline--in" : ""}`}
                  style={{ top: `${topOffset(step)}px` }}
                />
              ))}
            </div>

            <div className="rus-hub-bars">
              {HUB_ROWS.map((row, colIdx) => {
                const isHover = hover === row.key;
                const valuesB = row.pct.map((p) => (row.totalB * p) / 100);
                const displayTotal = mode === "billion" ? row.totalB : 100;
                return (
                  <div
                    className="rus-hub-col"
                    key={row.key}
                    onMouseEnter={() => setHover(row.key)}
                    onMouseLeave={() => setHover(null)}
                  >
                    <div className={`rus-hub-stack ${isHover ? "rus-hub-stack--hover" : ""}`}>
                      <div className="rus-hub-topper">
                        {row.showPlane && (
                          <svg
                            className="rus-hub-plane"
                            width="20" height="20" viewBox="0 0 24 24" fill="none"
                            stroke="#009A84" strokeWidth="1.7"
                          >
                            <path d="M12 2.5c.6 0 1 .5 1 1.2v5.1l6.8 4v1.7l-6.8-2.1v4.3l1.9 1.4v1.4l-2.9-.9-2.9.9v-1.4l1.9-1.4v-4.3L4.2 14.5v-1.7l6.8-4V3.7c0-.7.4-1.2 1-1.2z" />
                          </svg>
                        )}
                        <span>{fmtAxis(displayTotal)}</span>
                      </div>

                      {isHover && (
                        <div className="rus-mkt-tooltip">
                          <div className="rus-mkt-tooltip-total">{row.label}</div>
                          {HUB_CATS.map((cat, i) => (
                            <div key={cat} className="rus-mkt-tooltip-row">
                              <span style={{ background: HUB_COLORS[i] }} />
                              {cat}: {valuesB[i].toFixed(2)}B
                            </div>
                          ))}
                        </div>
                      )}

                      {row.pct.map((p, i) => {
                        const value = mode === "billion" ? valuesB[i] : p;
                        const h = active ? (value / cfg.max) * HUB_MAX_PX : 0;
                        return (
                          <div
                            key={i}
                            className="rus-hub-seg"
                            style={{ height: `${h}px`, background: HUB_COLORS[i], transitionDelay: `${colIdx * 0.1}s` }}
                          >
                            {mode === "percent" && p >= 8 && <span className="rus-hub-seg-label">{p}%</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="rus-mkt-labels">
          <div className="rus-mkt-labels-spacer" />
          <div className="rus-mkt-labels-row">
            {HUB_ROWS.map((row) => (
              <div key={row.key} className="rus-mkt-label">{row.label}</div>
            ))}
          </div>
        </div>
      </div>

      <div className="rus-legend-grid" style={{ marginTop: 18 }}>
        {HUB_CATS.map((cat, i) => (
          <div className="rus-legend-chip" key={cat}>
            <span className="rus-legend-dot" style={{ background: HUB_COLORS[i] }} /> {cat}
          </div>
        ))}
      </div>

     
    </div>
  );
}



interface Props {
  navigate: (p: Page) => void;
}

// ─── Multi-select générique — reproduit le widget "Origin Airport" /
// "Metro areas" du dashboard CARTO Builder : bouton "All selected" (ou
// "N selected"), menu déroulant avec cases à cocher, "All" en tête. ───
function MultiSelect({
  label,
  options,
  selected,
  onChange,
  allLabel = "All selected",
  loading = false,
}: {
  label: string;
  options: { value: string; label: string }[];
  selected: string[]; // liste vide = "All selected"
  onChange: (vals: string[]) => void;
  allLabel?: string;
  loading?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const allSelected = selected.length === options.length;
const summary = loading
  ? "Loading…"
  : allSelected
    ? allLabel
    : selected.length === 0
      ? "None selected"
      : `${selected.length} selected`;

const toggle = (v: string) => {
  if (selected.includes(v)) {
    onChange(selected.filter((x) => x !== v));
  } else {
    onChange([...selected, v]);
  }
};
const toggleAll = () => {
  if (allSelected) {
    onChange([]);
  } else {
    onChange(options.map((o) => o.value));
  }
};

  return (
    <div className="rus-ms" ref={ref}>
      <div className="rus-ms-label">{label}</div>
      <button className="rus-ms-btn" onClick={() => setOpen((o) => !o)} disabled={loading}>
        <span>{summary}</span>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2.5">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && !loading && (
        <div className="rus-ms-menu">
          <button className="rus-ms-option rus-ms-option--all" onClick={toggleAll}>
            <span className={`rus-ms-check ${allSelected ? "rus-ms-check--on" : ""}`} />
            {allLabel}
          </button>
          {options.map((o) => {
            const isOn = allSelected || selected.includes(o.value);
            return (
              <button key={o.value} className="rus-ms-option" onClick={() => toggle(o.value)}>
                <span className={`rus-ms-check ${isOn ? "rus-ms-check--on" : ""}`} />
                {o.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Slider double poignée — "Route GCD (NM)", min fixé à 100NM comme
// dans le widget CARTO Builder d'origine. ───
function GcdSlider({
  from,
  to,
  min = 100,
  max = GCD_NM_MAX,
  label = "Route GCD (NM)",
  onChange,
}: {
  from: number;
  to: number;
  min?: number;
  max?: number;
  label?: string;
  onChange: (from: number, to: number) => void;
}) {
  const pct = (v: number) => ((v - min) / (max - min)) * 100;

  return (
    <div className="rus-gcd">
      <div className="rus-gcd-label">{label}</div>
      <div className="rus-gcd-track-wrap">
        <div className="rus-gcd-track" />
        <div className="rus-gcd-range" style={{ left: `${pct(from)}%`, right: `${100 - pct(to)}%` }} />
        <input
          type="range"
          min={min}
          max={max}
          value={from}
          onChange={(e) => onChange(Math.min(Number(e.target.value), to - 10), to)}
          className="rus-gcd-input"
        />
        <input
          type="range"
          min={min}
          max={max}
          value={to}
          onChange={(e) => onChange(from, Math.max(Number(e.target.value), from + 10))}
          className="rus-gcd-input"
        />
      </div>
      <div className="rus-gcd-values">
        <span>{from} NM</span>
        <span>{to} NM</span>
      </div>
    </div>
  );
}

// ─── Section 3 "MobilityMonitor" — reproduit exactement l'architecture de
// Story.tsx Step3 (India) : composant de carte 3D en fond plein écran,
// dégradé de lisibilité, panneau de texte en haut à gauche, "Key figures"
// + recherche de ville en bas à gauche, légende/zoom rendus par le
// composant de carte lui-même (comme .mm-legend/.mm-zoom-controls côté
// India), et un déblocage du scroll de la page au-dessus du canvas 3D. ───
function MobilityMonitorStep({ active }: { active: boolean }) {
  const handleRef = useRef<MobilityMarketUSHandle | null>(null);
  const [selectedCity, setSelectedCity] = useState<FocusCity | null>(null);
  const [stats, setStats] = useState({ hexCount: 0, totalWeekly: 0, loading: true });

  // ── Filtres — mêmes paramètres que le dashboard CARTO Builder ──
  const [originSelected, setOriginSelected] = useState<string[]>([]); // [] = "All selected"
  const [metroSelected, setMetroSelected] = useState<string[]>([]); // [] = "All selected"
  const gcdFrom = 100;
const gcdTo = 400;

  // "Origin Airport" — chargé EN DIRECT depuis la table (SELECT DISTINCT
  // airport_city_catchment), pas depuis une liste statique devinée.
  const [originAllValues, setOriginAllValues] = useState<string[]>([]);
  const [originLoading, setOriginLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchUSCatchments()
      .then((vals) => {
        if (cancelled) return;
        setOriginAllValues(vals);
        setOriginSelected(vals);
        setOriginLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setOriginAllValues([]);
        setOriginLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const [metroOptions, setMetroOptions] = useState<{ value: string; label: string }[]>([]);
  const [metroLoading, setMetroLoading] = useState(false);

  const originOptions = originAllValues.map((v) => ({ value: v, label: v }));
const effectiveCatchments = originSelected;

  // Recharge dynamiquement la liste des métros disponibles selon les
  // catchments actuellement sélectionnés (comme le paramètre en cascade
  // du Builder CARTO).
  useEffect(() => {
    if (effectiveCatchments.length === 0) return;
    let cancelled = false;
    setMetroLoading(true);
    fetchUSMetroAreas(effectiveCatchments)
      .then((metros) => {
        if (cancelled) return;
        const options = metros.map((m) => ({ value: m, label: m }));

setMetroOptions(options);
setMetroSelected(metros);
        setMetroLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setMetroOptions([]);
        setMetroLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveCatchments.join("|")]);

  const filters: USHexFilters = {
    catchments: effectiveCatchments,
    metroAreas: metroSelected,
    gcdNmFrom: gcdFrom,
    gcdNmTo: gcdTo,
  };

  const zoomToCity = (city: FocusCity) => {
    setSelectedCity(city);
    handleRef.current?.zoomToCity(city);
  };
  /* moins en haut des textbox*/

const [textCollapsed, setTextCollapsed] = useState(false);
  const clearCity = () => {
    setSelectedCity(null);
    handleRef.current?.resetView();
  };

  return (
    <div className="rus-mmfull-wrap">
      <MobilityMarketUS
        filters={filters}
        onReady={(h) => (handleRef.current = h)}
        onStats={setStats}
      />

      <div className="rus-mmfull-overlay rus-mmfull-overlay--mobility" />

      {/* ── Texte en haut à gauche ── */}
      <div
  className={`rus-mmfull-text ${active ? "rus-in-up" : ""} ${
    textCollapsed ? "rus-mmfull-text--collapsed" : ""
  }`}

  onWheel={(e) => {
    e.stopPropagation();
  }}

  onMouseDown={(e) => {
    e.stopPropagation();
  }}

  onClick={(e) => {
    e.stopPropagation();
  }}

  onDoubleClick={(e) => {
    e.stopPropagation();
  }}
>
    <button
    className="rus-text-collapse"
    onClick={() => setTextCollapsed((v) => !v)}
    aria-label={textCollapsed ? "Afficher le texte" : "Réduire le texte"}
  >
    {textCollapsed ? "+" : "−"}
  </button>
        <div className="rus-kicker">Finding new demand</div>
        <h2 className="rus-h2">Finding air travel demand on the ground</h2>
        <p className="rus-p">
          We realized that much of the demand for regional aviation exists today, but it is travelling by car rather than by air. To better understand this opportunity, we developed <strong>MobilityMonitor</strong>, a new approach to mobility analysis.
        </p>
      
        <p className="rus-p">
         Using anonymized location data from <strong>80 million mobile phones</strong> in the United States, <strong>MobilityMonitor </strong> 
        allows us to generate new insights into how people move between cities, regardless of whether they travel by road or by air.  

        </p>
        <p className="rus-p">
         <strong>MobilityMonitor</strong> maps flows of people between <strong>470 geographical demand zones</strong> and allows us to measure aviation’s share on each of these flows.  
        </p>
    
      </div>

      {/* ── Key figures + recherche + filtres, en bas à gauche ── */}
      <div className={`rus-mmfull-bottom rus-mmfull-bottom-page2 ${active ? "rus-in-up" : ""}`}>
        <div className="rus-mmfull-filters">
      
          <MultiSelect
            label="Metropolitan areas / City"
            options={metroOptions}
            selected={metroSelected}
            onChange={setMetroSelected}
            loading={metroLoading}
          />
         
        </div>
      </div>
    </div>
  );
}

const FOCUS_CITIES = [
  { code: "MKE", city: "Milwaukee" },
  { code: "CVG", city: "Cincinnati" },
  { code: "CMH", city: "Columbus" },
  { code: "CLE", city: "Cleveland" },
  { code: "IND", city: "Indianapolis" },
  { code: "PIT", city: "Pittsburgh" },
  { code: "STL", city: "St. Louis" },
  { code: "RIC", city: "Richmond" },
  { code: "BNA", city: "Nashville" },
  { code: "RDU", city: "Raleigh" },
  { code: "MEM", city: "Memphis" },
  { code: "BHM", city: "Birmingham" },
  { code: "TYS", city: "Knoxville" },
  { code: "MSY", city: "New Orleans" },
  { code: "AVL", city: "Asheville" },
];

// ─── Sommaire de navigation — reproduit exactement l'architecture
// India (Story.tsx) : colonne fixe à gauche (pas un overlay), header,
// items avec puce+numéro+titre, barre de progression "X / Y" en bas. ───
const SECTIONS = [
  { id: "s1", number: "01", title: "The market size", accent: "#009A84" },
  { id: "s2", number: "02", title: "A regional decline", accent: "#009A84" },
  { id: "s3", number: "03", title: "Finding new demand", accent: "#009A84" },
  { id: "s4", number: "04", title: "The mobility gap", accent: "#009A84" },
  { id: "s5", number: "05", title: "Heartland opportunities", accent: "#009A84" },
  { id: "s7", number: "06", title: "Turboprops", accent: "#009A84" },
];

function StoryNav({
  activeStep,
  onNavigate,
  onBackToMenu,
}: {
  activeStep: number;
  onNavigate: (id: string) => void;
  onBackToMenu: () => void;
}) {
  const progress = ((activeStep + 1) / SECTIONS.length) * 100;

  return (
    <nav className="rus-nav">
      <div className="rus-nav-header">
        <div className="rus-nav-label">Mobility in the US</div>
      </div>

      <div className="rus-nav-items">
        {SECTIONS.map((s, i) => (
          <button
            key={s.id}
            className={`rus-nav-item ${activeStep === i ? "active" : ""}`}
            style={{ "--item-accent": s.accent } as React.CSSProperties}
            onClick={() => onNavigate(s.id)}
          >
            <span className="rus-nav-dot" />
            <span className="rus-nav-text">
              <span className="rus-nav-number">{s.number}</span>
              <span className="rus-nav-title">{s.title}</span>
            </span>
          </button>
        ))}
      </div>

      <button
        onClick={onBackToMenu}
        className="back-to-menu"
      >
        Back to menu
      </button>

      <div className="rus-nav-progress">
        <div className="rus-nav-progress-track">
          <div
            className="rus-nav-progress-fill"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="rus-nav-progress-label">
          {activeStep + 1} / {SECTIONS.length}
        </div>
      </div>
    </nav>
  );
}

export default function Story({ navigate }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoPlaying, setVideoPlaying] = useState(true);
  const [textCollapsed, setTextCollapsed] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [showFocusRoutes, setShowFocusRoutes] = useState(false);
  const [focusFilters, setFocusFilters] = useState({
  drive: false,
  indirect: false,
  direct: false,
});
  const scrollRef = useRef<HTMLDivElement>(null);

  // Détermine quelle section est actuellement centrée à l'écran, pour
  // mettre en avant le bon item + faire avancer la barre de progression
  // dans la sidebar — indépendant du fade-in au scroll de chaque Section
  // (threshold différent, plus strict ici).
  useEffect(() => {
    const root = scrollRef.current;
    if (!root) return;
    const sections = SECTIONS.map((s) => document.getElementById(s.id));
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = SECTIONS.findIndex((s) => s.id === entry.target.id);
            if (idx !== -1) setActiveStep(idx);
          }
        });
      },
      { root, threshold: 0.5 }
    );
    sections.forEach((s) => s && obs.observe(s));
    return () => obs.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="rus-root">
      <StoryNav
  activeStep={activeStep}
  onNavigate={scrollTo}
  onBackToMenu={() => navigate("usLanding")}
/>

      <div className="rus-scroll" ref={scrollRef}>
      {/* ── 1 · BIGGEST MOBILITY MARKET ── */}
      <Section id="s1" className="rus-split">
        {(v) => (
          <>
            <div className={`rus-split-left ${v ? "rus-in-up" : ""}`}>
              <div className="rus-kicker">The market size</div>
              <h2 className="rus-h2">The world's largest mobility market</h2>
              <p className="rus-p">
               With the world’s largest fleet of cars and aircraft, the United States generates more inter-city trips per year than any other country, with an estimated <strong>8 billion  one-way trips </strong>  made every year across all modes of transport.
              </p>

              <p className="rus-p">
                Nearly <strong> 6 billion</strong> of these trips are regional, covering distances under 400 NM.
                 </p>

                 
              <p className="rus-p">
                Yet despite its size, the regional travel market remains largely underserved by air transport. Only <strong>4% </strong> of regional travelers choose to fly, compared with <strong>30% </strong>for travellers on trips above 400 NM.  
              </p>
            
             
            </div>

            <div className={`rus-split-right ${v ? "rus-in-up rus-delay-2" : ""}`}>
              <MarketChart active={v} />
            </div>
          </>
        )}
      </Section>

      {/* ── 2 · 800 ROUTES CLOSED ── */}
      {/* ── 2 · 800 ROUTES CLOSED (dashboard 2D interactif) ── */}
      <Section id="s2" className="rus-mmfull">
        {(v) => <RoutesEvolutionStep active={v} />}
      </Section>

      {/* ── 3 · MOBILITYMONITOR (carte 3D plein écran, comme Story.tsx Step3 en Inde) ── */}
      <Section id="s3" className="rus-mmfull">
        {(v) => <MobilityMonitorStep active={v} />}
      </Section>

      {/* ── 4 · L HUB BIAS ── */}
      <Section id="s4" className="rus-split">
        {(v) => (
          <>
            <div className={`rus-split-left ${v ? "rus-in-up" : ""}`}>
              <div className="rus-kicker">The mobility gap</div>
              <h2 className="rus-h2">The mobility gap</h2>
              <p className="rus-p">
  To better understand how air transport serves domestic mobility demand, we compared passenger air travel patterns  with the country’s broader mobility flows.  
</p>
<p className="rus-p">
  We observe that <strong>90% of air passengers</strong> pass through a large hub airport during their journey. Yet only <strong>50% of overall mobility</strong> flows begin or end in a large hub.
</p>
<p className="rus-p">
 The remaining half of mobility is made up of people travelling between medium hubs, or from medium to smaller hubs. Today, <strong>the air system is only capturing a fraction of this demand</strong>.
</p>
<p className="rus-p">
  This disconnect between how people travel and how air services are structures creates what we call a <strong> mobility gap</strong>. 
Closing that gap represent a big opportunity for regional airlines in the United States.

</p>

            </div>
            <div className={`rus-split-right ${v ? "rus-in-up rus-delay-2" : ""}`}>
              <HubBiasChart active={v} />
            </div>
          </>
        )}
      </Section>

      {/* ── 5 · MIDDLE AMERICA MAP ── */}
<Section id="s5" className="rus-mmfull">
  {() => (
    <div className="rus-mmfull-wrap">
      <FocusCitiesMapUS
  showRoutes={showFocusRoutes}
  filters={focusFilters}
/>
        
        <div
  className={`rus-mmfull-text rus-in-up rus-focus-title ${
    textCollapsed ? "rus-mmfull-text--collapsed" : ""
  }`}

  onWheel={(e) => {
    e.stopPropagation();
  }}

  onMouseDown={(e) => {
    e.stopPropagation();
  }}

  onClick={(e) => {
    e.stopPropagation();
  }}

  onDoubleClick={(e) => {
    e.stopPropagation();
  }}
>
  <button
    className="rus-text-collapse"
    onClick={(e) => {
      e.stopPropagation();
      setTextCollapsed((v) => !v);
    }}
    aria-label={textCollapsed ? "Afficher le texte" : "Réduire le texte"}
  >
    {textCollapsed ? "+" : "−"}
  </button>
  
          <div className="rus-kicker">Heartland opportunities</div>
  <h2 className="rus-h2">Opportunities in "America's Heartland" </h2>
    <p className="rus-p">
   <strong>MobilityMonitor</strong> reveals significant opportunities to develop regional air services where strong demand exists on the ground. 
Our analysis identified a cluster of <strong>15 cities</strong> that stand out as the most promising growth markets. Among them, we can find Memphis, Milwaukee, or even Pittsburgh. 

   </p>

    <p className="rus-p">
      Far from being peripheral, these cities are important economic and cultural centers. From Oreo cookies to Wendy's burgers, country music to Bush's Beans, they're responsible for many items consumed in everyday life. Their industries build a diversity of goods and machinery: Super Hornet fighter jets and Harley Davidson motorcycles, NASA’s rockets and Mercedes-Benz automobiles. 
  </p>
   <p className="rus-p">
    Given the location of the area and its cultural and industrial importance, we chose to call it  <strong>"America's Heartland"</strong>.
   </p>
  <p className="rus-p">
    Yet compared to travelers from large hubs, travelers in these communities often face journeys that take <strong>three times</strong> longer than a comparable direct flight.
  </p>
  <p className="rus-p">
    Developing new regional air links with efficient <strong>turboprop aircraft</strong> could bring <strong>10 million passengers</strong> out of their cars and into the air transport system, saving time, improving connectivity and strengthening regional economies.
  </p>



{showFocusRoutes && (
  <div className="rus-focus-filters">
    <label>
      <input
        type="checkbox"
        checked={focusFilters.drive}
        onChange={() =>
          setFocusFilters({
            ...focusFilters,
            drive: !focusFilters.drive,
          })
        }
      />
      Drive
    </label>

    <label>
      <input
        type="checkbox"
        checked={focusFilters.indirect}
        onChange={() =>
          setFocusFilters({
            ...focusFilters,
            indirect: !focusFilters.indirect,
          })
        }
      />
      Indirect by air
    </label>

    <label>
      <input
        type="checkbox"
        checked={focusFilters.direct}
        onChange={() =>
          setFocusFilters({
            ...focusFilters,
            direct: !focusFilters.direct,
          })
        }
      />
      Direct with ATR 
    </label>
  </div>
)}
    
      </div>
      {showFocusRoutes && (
  <div className="rus-focus-info">
    {focusFilters.drive && (
      <p className="rus-focus-info-drive">
        5.5 hours drive
      </p>
    )}

    {focusFilters.indirect && (
      <p className="rus-focus-info-indirect">
        3.5 h average time indirect flight
      </p>
    )}

    {focusFilters.direct && (
      <p className="rus-focus-info-direct">
        1.2 h direct flight
      </p>
    )}
  </div>

)}
 </div>
     )} 


</Section>
              
  
 
      {/* ── 7 · 300 TURBOPROPS ── */}

      <Section id="s7" className="rus-split">
        {(v) => (
          <>
          <div className="rus-s7-bg-photo">
  <img src="/photos/Bridge_Airport_ATR_Corporate_2026.jpg" alt="" />
</div>
            <div className={`rus-split-left ${v ? "rus-in-up" : ""}`}>
              <div className="rus-kicker">Turboprops</div>
              <h2 className="rus-h2">Props are the way ! </h2>
              <p className="rus-p">
              ATR has identified a potential of <strong> 300 turboprops</strong> in the US. 
              </p>
              <p className="rus-p">
             <strong> Growing regional connectivity</strong> by encouraging a change of transport mode from car to plane in <strong>"America's Heartland"</strong> will generate demand for around 100 aircraft.
              </p>
              <p className="rus-p">
             At least another 100 turboprops will be needed to <strong>sustain the network</strong> currently flown by ageing regional jets. ATR doesn’t seek to replace all regional jets, but to provide a valuable complementary turboprop fleet: regional jets redeployed on longer sectors, and <strong>turboprops introduced on shorter sectors</strong> where their economics are unbeatable.
              </p>
              <p className="rus-p">

              Finally, another 100 turboprops can be used to open new direct routes where indirect air traffic is already strong. This will reduce air travel durations 1/3 of what they are today, "enhancing" regional air travel offer and stimulating traffic. 
 </p>
            <p className="rus-p">
                
             Overall, adding <strong> turboprops</strong> will enable to <strong> grow, sustain and improve regional aviation</strong>, providing a better experience for air passengers and enabling airlines to build a more efficient, <strong>sustainable and better-connected network</strong>.
              </p>
              
 </div>
 
             <div
        className={`rus-split-right ${
          v ? "rus-in-up rus-delay-2" : ""
        }`}
      >
        <div
  className={`rus-split-right ${
    v ? "rus-in-up rus-delay-2" : ""
  }`}
>
  <div className="rus-side-links">
    <button
  className="rus-side-link"
  onClick={() => {
    const video = videoRef.current;

    if (video) {
      video.muted = false;
      video.volume = 1;
      video.play().catch(() => {});
    }

    document.getElementById("rus-video")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }}
>
 Modern & comfortable: ATRs for the US
  
</button>

    <a
      href="https://www.atr-aircraft.com/regional-mobility/regional-air-mobility/regional-mobility-in-the-united-states-of-america/"
      target="_blank"
      rel="noopener noreferrer"
      className="rus-side-link"
    >
      Our analysis of regional mobility in the US
    </a>


    <a
      href="https://www.atr-aircraft.com/stories/reinvigorating-regional-air-connectivity-in-the-united-states/"
      target="_blank"
      rel="noopener noreferrer"
      className="rus-side-link"
    >
      Learn more about the RJ50 retirement challenge
    </a>

    <a
      href="/ATR_UsMarket_leaflet2025_digital.pdf"
      target="_blank"
      rel="noopener noreferrer"
      className="rus-side-link"
    >
      Download the brochure
    </a>


  
    

   <div className="rus-contact-line">
  For <strong> Sales & Marketing inquiries</strong> — <a href="mailto:christopher.jones@atr-aircraft.com">
    christopher.jones@atr-aircraft.com
  </a>
</div>
<div className="rus-contact-line">
  For <strong> Communication inquiries</strong> —  <a href="mailto:jeanne.caumont@atr-aircraft.com">
    jeanne.caumont@atr-aircraft.com
  </a>
</div>

  </div>
 
</div>

      </div>
      
     
    </>
    
  )}
</Section>
{/* VIDÉO ENTRE S7 ET LE FOOTER */}

<div id="rus-video" className="rus-video-band">

  <video
    ref={videoRef}
    className="rus-video-band-video"
    src="/photos/1839_Video_US-Mockup-Marketing-Tour_Generic_2026-06-11_reduced.mp4"
    autoPlay
    muted
    loop
    playsInline
    onPlay={() => setVideoPlaying(true)}
    onPause={() => setVideoPlaying(false)}
  />

  <button
    className="rus-video-playpause"
    onClick={() => {
      const video = videoRef.current;
      if (!video) return;

      if (video.paused) {
        video.play();
      } else {
        video.pause();
      }
    }}
    aria-label={videoPlaying ? "Pause video" : "Play video"}
  >
    {videoPlaying ? "Ⅱ" : "▶"}
  </button>

</div>





<div id="rus-video" className="rus-footer-band">
  <FooterPage onBackToTop={handleBackToTop} />
  </div>
</div>
</div>
);
 
}